import { useEffect, useMemo, useState, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select } from "@/components/base/FormControls";
import { cn } from "@/utils/cn";
import {
  ACTION_LABELS,
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
  countPermissions,
  type PermissionAction,
  type PermissionMap,
} from "@/store/permissions/catalog";
import { useRoles } from "@/store/permissions/RolesProvider";
import { useEmployes } from "../EmployesProvider";
import {
  EMPLOYE_STATUT_OPTIONS,
  type Employe,
  type EmployeInput,
  type EmployeStatut,
} from "../types";

interface EmployeFormProps {
  open: boolean;
  onClose: () => void;
  employe?: Employe | null;
}

interface FormState {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  role: string;
  statut: EmployeStatut;
  permissions: PermissionMap;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmployeForm({ open, onClose, employe }: EmployeFormProps) {
  const { addEmploye, updateEmploye } = useEmployes();
  const { roles, roleOptions, getRole } = useRoles();
  const [form, setForm] = useState<FormState>(() => blankState());
  const [errors, setErrors] = useState<Record<string, string>>({});

  function blankState(): FormState {
    // La création se fait par invitation : la fiche démarre en « invite »
    // jusqu'à ce que l'employé définisse lui-même son mot de passe.
    return { prenom: "", nom: "", email: "", telephone: "", role: "", statut: "invite", permissions: {} };
  }

  // Options du sélecteur de rôle : les rôles configurés + le rôle actuel de
  // l'employé s'il n'est pas (ou plus) configuré, pour ne jamais le perdre.
  const options = useMemo(() => {
    const base = roleOptions.length > 0 ? [...roleOptions] : [];
    if (employe?.role && !base.some((opt) => opt.value === employe.role)) {
      base.push({ value: employe.role, label: `${employe.role} (non configuré)` });
    }
    return base;
  }, [roleOptions, employe]);

  useEffect(() => {
    if (!open) return;
    if (employe) {
      setForm({
        prenom: employe.prenom,
        nom: employe.nom,
        email: employe.email,
        telephone: employe.telephone,
        role: employe.role,
        statut: employe.statut,
        permissions: { ...employe.permissions },
      });
    } else {
      const defaultRole = roles[0]?.code ?? "";
      setForm({
        prenom: "",
        nom: "",
        email: "",
        telephone: "",
        role: defaultRole,
        statut: "invite",
        permissions: roles[0] ? { ...roles[0].permissions } : {},
      });
    }
    setErrors({});
  }, [open, employe, roles]);

  const update = (key: keyof FormState, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  // Changement de rôle → les permissions sont recalculées d'après le rôle configuré.
  const changeRole = (role: string) =>
    setForm((prev) => ({ ...prev, role, permissions: getRole(role) ? { ...getRole(role)!.permissions } : {} }));

  const toggle = (module: string, action: PermissionAction, value: boolean) =>
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: { ...(prev.permissions[module] ?? {}), [action]: value },
      },
    }));

  const resetAndClose = () => {
    setForm(blankState());
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!form.prenom.trim()) nextErrors.prenom = "Le prénom est obligatoire.";
    if (!form.nom.trim()) nextErrors.nom = "Le nom est obligatoire.";
    if (!form.email.trim()) nextErrors.email = "L'email est obligatoire pour créer le compte d'accès.";
    else if (!EMAIL_REGEX.test(form.email.trim())) {
      nextErrors.email = "Indiquez une adresse email valide.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: EmployeInput = {
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      email: form.email.trim(),
      telephone: form.telephone.trim(),
      role: form.role,
      statut: form.statut,
      permissions: form.permissions,
    };

    if (employe) updateEmploye(employe.id, input);
    else addEmploye(input);
    resetAndClose();
  };

  const total = countPermissions(form.permissions);

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={employe ? "Modifier l'employé" : "Ajouter un employé"}
      description={
        employe
          ? "Renseignez la fiche puis ajustez les permissions par module. Le rôle applique ses permissions configurées, que vous pouvez réduire si besoin."
          : "Renseignez la fiche puis ajustez les permissions par module. Un e-mail d'invitation sera envoyé à l'employé pour qu'il crée lui-même son mot de passe."
      }
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="employe-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            {employe ? "Enregistrer les modifications" : "Envoyer l'invitation"}
          </button>
        </>
      }
    >
      <form id="employe-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Prénom" htmlFor="emp-prenom" required error={errors.prenom}>
            <Input id="emp-prenom" value={form.prenom} onChange={(e) => update("prenom", e.target.value)} placeholder="Ex. Fatou" maxLength={60} invalid={Boolean(errors.prenom)} />
          </Field>
          <Field label="Nom" htmlFor="emp-nom" required error={errors.nom}>
            <Input id="emp-nom" value={form.nom} onChange={(e) => update("nom", e.target.value)} placeholder="Ex. Traoré" maxLength={60} invalid={Boolean(errors.nom)} />
          </Field>
          <Field label="Email" htmlFor="emp-email" required error={errors.email}>
            <Input id="emp-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Ex. fatou@agence.bf" icon="ri-mail-line" maxLength={120} invalid={Boolean(errors.email)} />
          </Field>
          <Field label="Téléphone" htmlFor="emp-telephone">
            <Input id="emp-telephone" type="tel" value={form.telephone} onChange={(e) => update("telephone", e.target.value)} placeholder="Ex. +226 70 00 00 00" icon="ri-phone-line" maxLength={40} />
          </Field>
          <Field label="Rôle" htmlFor="emp-role" hint={options.length === 0 ? "Créez d'abord un rôle dans Paramètres → Rôles et permissions." : undefined}>
            <Select
              id="emp-role"
              value={form.role}
              onChange={(e) => changeRole(e.target.value)}
              options={options}
              placeholder={options.length === 0 ? "Aucun rôle configuré" : undefined}
              disabled={options.length === 0}
            />
          </Field>
          {employe && (
            <Field label="Statut" htmlFor="emp-statut">
              <Select id="emp-statut" value={form.statut} onChange={(e) => update("statut", e.target.value)} options={EMPLOYE_STATUT_OPTIONS} />
            </Field>
          )}
        </div>

        {!employe && (
          <div className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-xs leading-relaxed text-secondary-900">
            <i className="ri-mail-send-line mt-0.5 text-sm" aria-hidden="true" />
            <span>
              Un e-mail d'invitation sera envoyé à l'adresse indiquée. L'employé définira lui-même son mot de passe : vous n'avez aucun mot de passe à saisir ni à connaître.
            </span>
          </div>
        )}

        <div className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-shield-keyhole-line text-base" aria-hidden="true" />
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold text-foreground-950">Permissions par module</h3>
              <p className="text-xs text-foreground-500">
                {total} permission{total > 1 ? "s" : ""} active{total > 1 ? "s" : ""} · héritées du rôle, ajustables.
              </p>
            </div>
          </div>
          <div className="flex flex-col divide-y divide-background-200">
            {PERMISSION_MODULES.map((module) => (
              <div key={module.key} className="flex flex-col gap-2.5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                    <i className={cn(module.icon, "text-base")} aria-hidden="true" />
                  </span>
                  <span className="truncate text-sm font-label font-medium text-foreground-900">{module.label}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {PERMISSION_ACTIONS.filter((action) => module.actions.includes(action)).map((action) => {
                    const active = Boolean(form.permissions[module.key]?.[action]);
                    return (
                      <button
                        key={action}
                        type="button"
                        onClick={() => toggle(module.key, action, !active)}
                        aria-pressed={active}
                        className={cn(
                          "inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[11px] font-label font-medium transition-colors cursor-pointer",
                          active
                            ? "border-primary-300 bg-primary-100 text-primary-900"
                            : "border-background-200 bg-background-50 text-foreground-500 hover:bg-background-100"
                        )}
                      >
                        <i className={cn(active ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line", "text-sm")} aria-hidden="true" />
                        {ACTION_LABELS[action]}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}